import { maybe, rd } from '@passionware/monads';
import { uniq } from 'lodash';
import type { Resource } from '__types__/v1/models';
import { Calendar, CalendarCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { BooleanFilter } from 'v5/api/_common/query/filters/BooleanFilter';
import type { ResourceSearchQuery } from 'v5/api/resource/resource.api';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import { Badge } from 'v5/design-system/Badge';
import { createResourceTypeFilterConfig } from 'v5/features/list-filters/metastructure-filters/core/resource-type';
import { useResourceFilters } from 'v5/features/resources/useResourceFilters';
import { ResultFieldsButton } from 'v5/features/resource-fields/ResultFieldsButton';
import { useResultFields } from 'v5/features/resource-fields/useResultFields';
import type {
  FilterBooleanConfig,
  FilterConfigBase
} from 'v5/patterns/filters/FilterWidget.types';
import { ResourcePicker } from 'v5/patterns/resource-picker/ResourcePicker';
import type { ResourcePickerResultsProps } from 'v5/patterns/resource-picker/ResourcePickerResults';
import { useResourcePickerView } from 'v5/patterns/resource-picker/useResourcePickerView';
import { useSelector } from 'react-redux';
import type { Nullable } from 'v5/platform/typescript';
import {
  type AssignResourceDialogServices,
  conflictDateLabel,
  conflictingBookingsOf,
  firstConflictStartOf,
  parseDate
} from './assignResourceCandidate';
import { productionScopeOf } from './productionScope.logic';
import { useAssignmentCandidateContext } from './useAssignmentCandidateContext';

/** The toolbar's query, with the production's own "Available" switch among its filters. */
type ProductionPickerQuery = ResourceSearchQuery & {
  filters: ResourceSearchQuery['filters'] & {
    available?: Nullable<BooleanFilter>;
  };
};

/** A search with the production's "Available" chip on or off, beside the resource filters. */
export function withAvailableChip(
  query: ResourceSearchQuery,
  chip: Nullable<BooleanFilter>
): ProductionPickerQuery {
  // the chip is no resource filter: the filters' index signature can't hold it
  return (
    chip ? { ...query, filters: { ...query.filters, available: chip } } : query
  ) as ProductionPickerQuery;
}

/**
 * Searching resources for a production's slot: the resources of the production's workspace (of
 * the organization for a production of none), "Available" in the production's dates, and each
 * result's bookings that clash with them. The query is the host's: what it starts from and
 * whether it is kept.
 */
export function useProductionResourcePicker({
  services,
  productionId,
  query,
  onQueryChange
}: {
  services: AssignResourceDialogServices;
  productionId: number;
  query: ProductionPickerQuery;
  onQueryChange: (query: ProductionPickerQuery) => void;
}) {
  const { t } = useTranslation('productions');
  const { view, onViewChange } = useResourcePickerView(services);
  const browsingScope = rd.tryMap(
    services.appScopeService.useResolvedAppScope(),
    appScopeUtils.identityFromResolved
  );
  const filtersRd = useResourceFilters(
    maybe.map(browsingScope, scope => ({ type: 'resource' as const, scope })),
    services
  );
  const resultFields = useResultFields(services);
  const { assignmentCandidateContext, bookingDateRangeLabel } =
    useAssignmentCandidateContext(productionId);
  const { productionDate, productionRange } = assignmentCandidateContext;
  // the production's own workspace: its record first, then the v1 store's copy of it
  const productionRd = services.productionService.useProduction(
    productionId,
    services.appScopeService.useOrganizationId()
  );
  const storeCopy = useSelector(
    (state: {
      productions?: {
        data?: Record<number, { enterprise_workspace_id?: number | null }>;
      };
    }) => state?.productions?.data?.[productionId]
  );
  const productionScope = rd.isError(productionRd)
    ? productionScopeOf(undefined, storeCopy)
    : rd.tryMap(productionRd, record => productionScopeOf(record, storeCopy));
  const scope =
    productionScope?.type === 'known' ? productionScope.scope : undefined;

  const availableFilter: FilterBooleanConfig | null =
    productionDate?.start_date &&
    productionDate.end_date &&
    bookingDateRangeLabel
      ? {
          label: t('resourcingAssignDialogAvailable', {
            dates: bookingDateRangeLabel,
            interpolation: { escapeValue: false }
          }),
          icon: <CalendarCheck />,
          type: 'boolean',
          createDefaultValue: () => ({ operator: 'equal', value: true }),
          options: {
            valueLabel: t('resourcingAssignDialogAvailableValue'),
            subjectLabel: t('resourcingAssignDialogAvailableSubject')
          }
        }
      : null;
  const filterConfig: Record<string, FilterConfigBase> = {
    // a slot's saved search names its resource type: shown as the type, when the scope's fields
    // don't offer the filter themselves
    ...(browsingScope && {
      resource_type_id: createResourceTypeFilterConfig(services, browsingScope)
    }),
    ...(rd.tryMap(filtersRd, filters => filters.configs) ?? {}),
    ...(availableFilter && { available: availableFilter })
  };
  const { available: availableChip, ...searchFilters } = query.filters;
  const searchQuery = { ...query, filters: searchFilters };
  const listQuery =
    availableChip?.value === true &&
    productionDate?.start_date &&
    productionDate.end_date
      ? {
          ...searchQuery,
          available: {
            startDate: productionDate.start_date,
            endDate: productionDate.end_date,
            statusIds: null
          }
        }
      : searchQuery;

  return {
    services,
    query,
    onQueryChange,
    scope,
    /** The production could not be read: whose resources to offer is not known. */
    scopeUnknown: productionScope?.type === 'unknown',
    view,
    onViewChange,
    resultFields,
    filtersRd,
    filterConfig,
    availableFilter,
    pickerQuery: resultFields.queryOf(listQuery),
    assignmentCandidateContext,
    bookingDateRangeLabel,
    productionRange,
    /** A result's bookings in the production's dates: the first one's day, or how many. */
    conflictBadge: (resource: Resource) => {
      const conflicts = conflictingBookingsOf(resource, productionRange);
      if (conflicts.length === 0) return null;
      const first = firstConflictStartOf(conflicts);
      return (
        <Badge variant="attention" tone="subtle" size="sm" className="shrink-0">
          <Calendar className="size-3.5" />
          {first
            ? conflictDateLabel(first)
            : t('resourcingAssignDialogConflictCount', {
                count: conflicts.length
              })}
        </Badge>
      );
    }
  };
}

type ProductionResourcePickerModel = ReturnType<
  typeof useProductionResourcePicker
>;

/** The toolbar and the count; the host's notes (what the search left out) below them. */
function ProductionResourcePickerSearch({
  picker,
  children
}: {
  picker: ProductionResourcePickerModel;
  children?: ReactNode;
}) {
  const { filtersRd, availableFilter } = picker;
  return (
    <ResourcePicker.Header>
      <ResourcePicker.Toolbar
        query={picker.query}
        scope={picker.scope}
        onQueryChange={picker.onQueryChange}
        filters={{
          config: picker.filterConfig,
          groups: rd.tryMap(filtersRd, filters => filters.groups) ?? [],
          order: uniq([
            // a field set but out of the order isn't drawn at all: the type comes first
            ...(picker.filterConfig.resource_type_id
              ? ['resource_type_id']
              : []),
            ...(rd.tryMap(filtersRd, filters => filters.order) ?? []),
            ...(availableFilter ? ['available'] : [])
          ]),
          suggested: availableFilter
            ? ['locations', 'available']
            : ['locations']
        }}
      />
      <ResourcePicker.Summary
        actions={
          <>
            <ResultFieldsButton fields={picker.resultFields} />
            <ResourcePicker.ViewSwitch
              view={picker.view}
              onViewChange={picker.onViewChange}
            />
          </>
        }
      >
        <ResourcePicker.Count
          query={picker.pickerQuery}
          scope={picker.scope}
          services={picker.services}
        />
      </ResourcePicker.Summary>
      {children}
    </ResourcePicker.Header>
  );
}

/** The results with their clashes; what a check means and who is marked are the host's. */
function ProductionResourcePickerResults({
  picker,
  ...props
}: { picker: ProductionResourcePickerModel } & Pick<
  ResourcePickerResultsProps,
  'source' | 'onOpen' | 'openId' | 'selection' | 'present'
>) {
  const { t } = useTranslation('productions');
  if (picker.scopeUnknown) {
    return (
      <p role="status" className="p-6 text-sm text-fg-muted">
        {t('resourcingAssignDialogWorkspaceUnknown')}
      </p>
    );
  }
  return (
    <ResourcePicker.Results
      {...props}
      query={picker.pickerQuery}
      scope={picker.scope}
      view={picker.view}
      services={picker.services}
      marksOf={picker.conflictBadge}
      describe={picker.resultFields.describe}
      emptyState={
        <p className="p-6 text-sm text-fg-muted">
          {t('resourcingAssignDialogNoResourcesFound')}
        </p>
      }
    />
  );
}

/** The bookings that clash with the production, above the resource's own details. */
function ProductionResourcePickerDetails({
  picker,
  resource,
  children
}: {
  picker: ProductionResourcePickerModel;
  resource: Resource;
  children: ReactNode;
}) {
  const { t } = useTranslation('productions');
  const conflicts = conflictingBookingsOf(resource, picker.productionRange);
  const dateOf = (value: unknown) => {
    const date = parseDate(value);
    return date ? conflictDateLabel(date) : null;
  };
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {conflicts.length > 0 && (
        <section className="flex flex-col gap-1.5 border-b border-border-default p-4">
          <h3 className="text-xs font-semibold text-fg-muted">
            {t('resourcingAssignDialogBookingsInDates')}
          </h3>
          <ul className="flex flex-col gap-1">
            {conflicts.map(booking => {
              const start = dateOf(booking.start_date);
              const end = dateOf(booking.end_date ?? booking.start_date);
              return (
                <li key={booking.id} className="flex items-center gap-1.5">
                  <Badge variant="attention" tone="subtle" size="sm">
                    <Calendar className="size-3.5" />
                    {start === end ? start : `${start} – ${end}`}
                  </Badge>
                </li>
              );
            })}
          </ul>
        </section>
      )}
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
}

export const ProductionResourcePicker = {
  Search: ProductionResourcePickerSearch,
  Results: ProductionResourcePickerResults,
  Details: ProductionResourcePickerDetails
};
